import React from 'react';
import DashboardIcon from '../assets/svgs/nav/Dashboard.svg?react';
import AccountsPayableIcon from '../assets/svgs/nav/AccountsPayable.svg?react';
import PurchaseOrderIcon from '../assets/svgs/nav/PurchaseOrder.svg?react';
import DocumentsIcon from '../assets/svgs/nav/Documents.svg?react';
import ApprovalsIcon from '../assets/svgs/nav/Approvals.svg?react';
import PostingTracker from '../assets/svgs/nav/PostingTracker.svg?react';
import PaymentTracker from '../assets/svgs/nav/PaymentTracker.svg?react';
import TravelAuthIcon from '../assets/svgs/nav/TravelAuthorization.svg?react';
import { Optional } from 'common/types/utility';
import { AppRouter } from 'common/types/globalTypes';
import { useQuery } from '@apollo/client';
import { CommonDataOnAppLoading } from 'common/graphql/__generated__/CommonDataOnAppLoading';
import COMMON_APP_DATA from 'common/graphql/CommonDataOnAppLoading.graphql';

type SVGIcon = React.FunctionComponent<
  React.SVGProps<SVGSVGElement> & { title?: string }
>;
type FontIconName = string;

export type SVGIconOrFontIconName = SVGIcon | FontIconName;

export interface INavItem {
  name: string;
  Icon: SVGIconOrFontIconName;
  path: string;
  exact?: boolean;
  restricted?: 'administrator' | 'supervisor';
  routeName?: AppRouter | undefined;
  count?: number | undefined | null;
}

export type INavGroup = Optional<INavItem, 'path'> & {
  items?: INavItem[];
};

export const getUserPrivilegedNav = (
  navGroups: INavGroup[],
  privileges: Set<string>
) => {
  const newNavGroups: INavGroup[] = navGroups
    .map((navItem) => {
      const items = navItem.items?.filter(
        (item) =>
          privileges.has(item.routeName!) || item.routeName === undefined
      );
      const newNavItem: INavGroup = { ...navItem, items };
      return newNavItem;
    })
    .filter((item) => item?.items?.length! > 0 || item.items === undefined)
    .filter(
      (item) =>
        (item.routeName && privileges.has(item.routeName)) ||
        item.routeName === undefined
    );

  return newNavGroups;
};

export const useAppNav = () => {
  const { data } = useQuery<CommonDataOnAppLoading>(COMMON_APP_DATA);

  const getNavGroups = (privileges: Set<string>) => {
    const navGroups: INavGroup[] = [
      {
        name: 'Dashboard',
        path: '/',
        exact: true,
        Icon: DashboardIcon,
      },
      {
        name: 'Documents',
        Icon: DocumentsIcon,
        items: [
          {
            name: 'Document Assignment',
            path: '/doc/documentAssignment',
            Icon: 'BulkUpload',
            routeName: 'DOCUMENT_POOL_FILE_LOADER',
          },
          {
            name: 'My Documents',
            path: '/doc/personal',
            Icon: 'TextDocumentShared',
            routeName: 'PRIVATE_POOL',
          },
          {
            name: 'Folder Setup',
            path: '/doc/groups',
            Icon: 'FolderOpen',
            routeName: 'DOCUMENT_POOL',
          },
          {
            name: 'File Cabinet',
            path: '/doc/fileCabinet',
            Icon: 'DocumentSet',
            routeName: 'FILE_CABINET',
          },
        ],
      },
      {
        name: 'Transaction Signing',
        Icon: AccountsPayableIcon,
        routeName: 'TRANSACTION_SIGNING',
        path: '/signing',
      },
      {
        name: 'Approvals',
        Icon: ApprovalsIcon,
        count: data?.userPendingApprovalCount,
        items: [
          {
            name: 'My Approvals',
            path: '/approvals',
            Icon: 'InboxCheck',
            routeName: 'APPROVALS',
          },
          {
            name: 'All Approvals',
            path: '/approval_history',
            Icon: 'CheckList',
            restricted: 'supervisor',
            routeName: 'APPROVAL_HISTORY',
          },
        ],
      },
      {
        name: 'Posting Tracker',
        Icon: PostingTracker,
        items: [
          {
            name: 'Batch/Edit',
            path: '/postingTracker/batches',
            Icon: 'InsertSignatureLine',
            routeName: 'BATCH_EDIT',
          },
          {
            name: 'Transaction History',
            path: '/postingTracker/history',
            Icon: 'FullHistory',
            routeName: 'POSTED_TRANSACTIONS',
          },
        ],
      },

      {
        name: 'Payment Tracker',
        Icon: PaymentTracker,
        items: [
          {
            name: 'Pay Cycles',
            path: '/pay/payment_cycle',
            Icon: 'Transition',
            routeName: 'PAY_CYCLE',
          },
          {
            name: 'Payments',
            path: '/pay/payment_tracking',
            Icon: 'Money',
            routeName: 'PAYMENT_TRACKING',
          },
        ],
      },
      {
        name: 'Purchase Orders',
        Icon: PurchaseOrderIcon,
        routeName: 'PURCHASE_ORDER',
        path: '/purchase-orders',
      },

      {
        name: 'Travel Authorization',
        Icon: TravelAuthIcon,
        items: [
          {
            name: 'Travel Plan',
            Icon: 'Airplane',
            routeName: 'TRAVEL_AUTHORIZATION',
            path: '/ta/travel-plan',
          },
          {
            name: 'Trip Breakdown',
            Icon: 'AirTickets',
            routeName: 'TRAVEL_AUTHORIZATION',
            path: '/ta/trip-breakdown',
          },
        ],
      },
    ];
    const navGroupsPrivileged: INavGroup[] = getUserPrivilegedNav(
      navGroups,
      privileges
    );

    return navGroupsPrivileged;
  };

  return { getNavGroups };
};

export const bottomNavItems: INavItem[] = [
  {
    name: 'Project settings',
    Icon: 'Settings',
    path: '/project-settings',
    restricted: 'supervisor',
  },
  {
    name: 'Control center',
    Icon: 'Admin',
    path: '/account-management',
    restricted: 'administrator',
  },
];
